const { default: getFrontConfig } = require('../common/getFrontConfig');
const { getProductTypeOfID } = require('../common/helpers/productIdHelpers');
const { arrify } = require('../common/utils/arrayUtils');
const { isString } = require('../common/utils/type');
const { assignUpidToProduct } = require('../common/helpers/productModelHelpers');
const { analyticsEventCenter } = require('./events');

const FRONT_CONFIG = getFrontConfig();
const UNDEFINED = '__UNDEFINED__';
const ACTIVITY_TYPE = {
  VIEW_CATEGORY: 'viewCategory',
  VIEW_PRODUCT: 'viewProduct',
  VIEW_RECOMMENDATION: 'viewReco',
  VIEW_SEARCH: 'viewSearch',
};

/**
 * It takes a product and a view, and returns an activity
 * @param {Object} viewProduct - { id, sku, type, alt_id } The product that was viewed.
 * @param {Object} view - The view object that was passed to the view function.
 * @returns {Object} An object with the following properties:
 * recommenderName, refinements, product, categoryId, cookieId, userId, oldUserId, emailId,
 * locale, personalized, sortingRule, realm, siteId, instanceType, searchText, imageUUID
 */
function buildActivity(viewProduct, view) {
  return {
    recommenderName: view.recommenderName,
    refinements: view.refinements,
    product: viewProduct,
    categoryId: view.categoryId,
    cookieId: view.cookieId,
    userId: view.userId,
    oldUserId: view.oldUserId,
    emailId: view.emailId,
    locale: view.locale,
    personalized: view.personalized,
    sortingRule: view.sortingRule,
    realm: view.realm,
    siteId: view.siteId,
    instanceType: view.instanceType,
    searchText: view.searchText,
    imageUUID: view.imageUUID,
  };
}

/**
 * @typedef {object} CQProductParams
 * @property {string} id - ID of the product
 * @property {string} sku - SKU
 * @property {string} type - type of the product ('vgroup'...)
 * @property {alt_id} alt_id - ID of the variation group if there is type
 */

/**
 * It takes a product object and returns an object with the product's id, sku, type, and alt_id
 * @param {FullProduct} product - The product object in PDP
 * @returns {CQProductParams} Product params for cq
 */
function buildProductParams(product) {
  const productParams = {
    id: product.id,
    sku: '',
    type: '',
    alt_id: '',
  };
  if (product.productType === 'variationGroup') {
    if (!product.upid) {
      // eslint-disable-next-line no-param-reassign
      product = assignUpidToProduct(product);
    }
    productParams.id = product.upid;
    productParams.type = 'vgroup';
    productParams.alt_id = product.id;
  }
  return productParams;
}

/**
 * Returns an object with the info for cquotient
 * @param {string} productID - The product ID
 * @returns {CQProductParams} Product params for cq
 */
function buildProductParamsOfID(productID) {
  let product = {
    id: productID,
    productType: getProductTypeOfID(productID),
  };
  product = assignUpidToProduct(product);
  return buildProductParams(product);
}

/**
 * It returns an object with the CQuotient cookie ID, user ID, hashed email, hashed login, realm, site
 * ID, instance type, and locale
 * @returns {Object} An object with the following properties:
 * cookieId, userId, emailId, loginId, realm, siteId, instanceType, locale
 */
function cqBaseParams() {
  const cq = window.CQuotient;
  return {
    cookieId: cq.getCQCookieId(),
    userId: cq.getCQUserId(),
    emailId: cq.getCQHashedEmail(),
    loginId: cq.getCQHashedLogin(),
    realm: cq.realm,
    siteId: cq.siteId,
    instanceType: cq.instanceType,
    locale: cq.locale,
  };
}

/**
 * It takes an array of refinements and returns a stringified JSON object
 * @param {Object[]} refinements - The array of refinements that you want to pass to the CQ API.
 * @returns {string} A stringified array of objects.
 */
function cqRefinementsArray(refinements) {
  const cqRefinements = [];
  const refinementTypes = {
    category: /* Category refinement sets the value as refinement.id */
    (refinement) => {
      cqRefinements.push({
        name: 'Category',
        value: refinement.id,
      });
    },

    price: /* Price refinement generates three refinement objects in cquotient (priceMin, priceMax and curr) */
    (refinement) => {
      cqRefinements.push({
        name: 'priceMin',
        value: refinement.valueFrom.toString(),
      },
      {
        name: 'priceMax',
        value: refinement.valueTo.toString(),
      },
      {
        name: 'curr',
        value: FRONT_CONFIG.defaultCurrency,
      });
    },

    attribute: /* Attribute refinement sets name as refinement.id (attr id) */
    (refinement) => {
      cqRefinements.push({
        name: refinement.id,
        value: refinement.value,
      });
    },
  };

  /* Iterating over the refinements array and calling the appropriate function based on the refinement
  type. */
  refinements.forEach((refinement) => {
    if (refinement.type in refinementTypes) {
      return refinementTypes[refinement.type](refinement);
    }
    return refinementTypes.attribute(refinement);
  });

  return JSON.stringify(cqRefinements);
}

/**
 * It takes a categoryId, an array of refinements, and a sortingRule, and returns an object with the
 * same properties, but with the refinements in a format that CQuotient expects
 * @param {Object} - { category, refinements, sortingRule }
 * @returns {Object} An object with the categoryId, refinements, and sortingRule.
 */
function cqViewCategoryParams({ category, refinements, sortingRule }) {
  // When PLP is a subcategory, there is no category on selected filters
  // We need to track category always, so we create a fake refinement
  // with the subcategory needed data
  const categoryRefinement = refinements.some((refinement) => refinement.type === 'category');
  if (!categoryRefinement) {
    refinements.push({
      type: 'category',
      id: category.id,
    });
  }
  const cqRefinements = cqRefinementsArray(refinements);
  return {
    categoryId: category.id,
    refinements: cqRefinements,
    sortingRule: sortingRule !== 'MW' ? sortingRule : 'best-matches',
  };
}

/**
 * It takes a searchText parameter and returns an object with the searchText parameter as the value for
 * the searchText key
 * @param {string} searchText - The text to search for.
 * @returns {Object} An object with the following properties:
 * {
 *    refinements: '[]',
 *    searchText: 'searchText',
 *    suggestedSearchText: UNDEFINED,
 *    showProducts: 'true',
 *    sortingRule: 'Search',
 * }
 */
function cqViewSearchParams(searchText) {
  return {
    refinements: '[]',
    searchText,
    suggestedSearchText: UNDEFINED,
    showProducts: 'true',
    sortingRule: 'Search',
  };
}

/**
 * It returns the id of a product, but if the product is a vgroup, set, or bundle, it returns the
 * alt_id instead
 * @param {Object} product - { id, sku, type, alt_id } The product object.
 * @returns {string} The id of the product.
 */
function getIdByProductType(product) {
  const idByProductType = {
    vgroup: 'alt_id',
    set: 'alt_id',
    bundle: 'alt_id',
  };
  return product[idByProductType[product.type] || 'id'];
}

/**
 * It gets the session storage item for the given key, parses it as JSON, and returns the result
 * @param {string} key - The key to use for the session storage.
 * @returns {Object[]} An array of views.
 */
function getSessionViews(key) {
  const sessionView = window.sessionStorage.getItem(`cq.${key}`);
  try {
    return JSON.parse(sessionView);
  } catch (error) {
    return [];
  }
}

/**
 * Find the view that has a product with the same id as the viewProductId
 * @param {Object[]} views - The array of views that are currently in the session
 * @param {string} viewProductId - The product ID of the productView event
 * @returns {Object} The first view that has a product with the same id as the viewProductId
 */
function getSessionViewWithProduct(views, viewProductId) {
  return views.find((view) => view.products.some((product) => {
    const sessionViewProductId = getIdByProductType(product);
    return sessionViewProductId && viewProductId && sessionViewProductId === viewProductId;
  }));
}

/**
 * It sends an activity to CQuotient
 * @param {string} activityType - The type of activity you want to send.
 * @param {Object} cqParams - This is an object that contains the parameters that you want to send to CQuotient.
 */
function sendActivity(activityType, cqParams) {
  const cq = window.CQuotient;
  if (cq.sendActivity) {
    cq.sendActivity(cq.clientId, activityType, cqParams);
  } else {
    cq.activities.push({ activityType, parameters: cqParams });
  }
}

/**
 * It sends an activity to cqoutient if the product view is stored in sessionStorage
 * @param {Object} product - The product in PDP that was clicked.
 */
function sendActivityClickProduct(product) {
  const activities = {
    viewReco: '_clickReco',
    viewSearch: 'clickSearch',
    viewCategory: 'clickCategory',
  };

  const viewProduct = buildProductParams(product);
  const viewProductId = getIdByProductType(viewProduct);

  Object.keys(activities).forEach((key) => {
    const sessionViews = getSessionViews(key);
    if (sessionViews) {
      const sessionViewWithProduct = getSessionViewWithProduct(sessionViews, viewProductId);
      if (sessionViewWithProduct) {
        const activity = buildActivity(viewProduct, sessionViewWithProduct);
        sendActivity(activities[key], activity);
      }
    }
  });
}

/**
 * Send an activity to CQuotient with the given product IDs, activity type, and activity parameters.
 * @param {FullProduct[]|string[]} products - An array of products or product IDs.
 * @param {string} activityType - The type of activity.
 * @param {Object} activityParams - This is an object that contains any additional parameters you want to send
 * to CQuotient.
 */
function sendActivityProductShown(
  products,
  activityType = ACTIVITY_TYPE.VIEW_CATEGORY,
  activityParams = {},
) {
  try {
    const cq = window.CQuotient;
    if (cq) {
      const cqParams = cqBaseParams();

      const productArray = arrify(products);
      const isArrayOfIDs = isString(productArray[0]);
      const transformer = isArrayOfIDs ? buildProductParamsOfID : buildProductParams;
      cqParams.products = productArray.map(transformer);

      cqParams.accumulate = true;
      cqParams.personalized = 'false';
      cqParams.imageUUID = UNDEFINED;

      Object.assign(cqParams, activityParams);

      sendActivity(activityType, cqParams);
    }
  } catch (err) {
    // continue regardless of error
  }
}

/**
 * It takes a product and an activity type, and sends the activity to CQuotient
 * @param {Object} product - The product object that is being viewed.
 * @param {string} activityType - The type of activity you want to send.
 */
function sendActivityViewProduct(product, activityType) {
  const cqParams = cqBaseParams();
  cqParams.product = buildProductParams(product);

  sendActivity(activityType, cqParams);
}

/**
 * This function sends a view product activity to CQuotient.
 * @param {FullProduct} product - The product object that you want to track.
 */
function trackViewProduct(product) {
  sendActivityClickProduct(product);
  sendActivityViewProduct(
    product,
    ACTIVITY_TYPE.VIEW_PRODUCT,
  );

  if (product?.bylCompleteTheLook?.length > 0) {
    trackViewProductInReco(
      product.bylCompleteTheLook.map((item) => item.product),
    );
  }

  if (product?.bylExplicitRecommendations?.length > 0) {
    trackViewProductInReco(
      product.bylExplicitRecommendations.map((item) => item.product),
    );
  }

  if (product?.bylEinsteinRecommendations?.length > 0) {
    trackViewProductInReco(
      product.bylEinsteinRecommendations.map((item) => item.product),
      {
        recommenderName: FRONT_CONFIG.einstein.recommenderName,
      },
    );
  }
}

/**
 * When a category is viewed, send an activity to CQuotient.
 * @param {string[]} productIDs - An array of product IDs.
 * @param {Object} categoryData - { categoryId, refinements, sortingRule }
 */
function trackViewProductInCategory(productIDs, categoryData) {
  sendActivityProductShown(
    productIDs,
    ACTIVITY_TYPE.VIEW_CATEGORY,
    cqViewCategoryParams(categoryData),
  );
}

/**
 * When a user views the search results, send an activity to CQuotient."
 * @param {string[]} productIDs - An array of product IDs.
 * @param {string} searchText - The search text that the user entered.
 */
function trackViewProductInSearch(productIDs, searchText) {
  sendActivityProductShown(
    productIDs,
    ACTIVITY_TYPE.VIEW_SEARCH,
    cqViewSearchParams(searchText),
  );
}

/**
 * When a product is shown in a recommendation, send an activity to CQuotient.
 * @param {FullProduct[]} products - An array of products
 * @param {Object} [params] - An object of params of the activity
 * @param {string} [params.recommenderName] - Recommender name
 */
function trackViewProductInReco(products, params = {}) {
  sendActivityProductShown(products, ACTIVITY_TYPE.VIEW_RECOMMENDATION, params);
}

/*
    //Sample SFRA snippet
    (function(){
        try {
            if(window.CQuotient) {
                var cq_params = {};

                cq_params.cookieId = window.CQuotient.getCQCookieId();
                cq_params.userId = window.CQuotient.getCQUserId();
                cq_params.accumulate = true;
                cq_params.products = [{
                    id: '371570',
                    sku: '',
                    type: '',
                    alt_id: ''
                }];

                cq_params.recommenderName = 'product-to-product-eu';
                cq_params.realm = "AAKF";
                cq_params.siteId = "EU";
                cq_params.instanceType = "prd";

                if(window.CQuotient.sendActivity)
                    window.CQuotient.sendActivity( CQuotient.clientId, 'viewReco', cq_params );
                else
                    window.CQuotient.activities.push( {activityType: 'viewReco', parameters: cq_params} );
            }
        } catch(err) {}
    })();
*/

function init() {
  analyticsEventCenter.subscribe(
    analyticsEventCenter.EVENTS.productDetailsViewed,
    (data) => {
      if (data?.product?.productType === 'variationGroup') {
        trackViewProduct(data.product);
      }
    },
  );
}

module.exports = {
  init,
  trackViewProduct,
  trackViewProductInCategory,
  trackViewProductInReco,
  trackViewProductInSearch,
};
